import type { MsgKey } from "./i18n";

/**
 * 写真拼图的前端模型。
 *
 * 两个数各管各的，别在界面上把它们绑在一起：
 * - **碎片价**是唯一的经济量，兑换只看它；
 * - **拼块数**（`gridN²`）是纯表现量，只决定揭图动画的网格切法。
 * 默认相等（第 1 张 9 片 / 9 碎片），但**允许配成不等**。
 *
 * 价格与网格都由 `seq` 查全局价目表带出，**不能在单张拼图上改** ——
 * 「热门 idol 更贵」是明确要杜绝的差别待遇。
 */

export type PhotoType = "normal" | "stage" | "limited";
export const PHOTO_TYPES: PhotoType[] = ["normal", "stage", "limited"];
export const PHOTO_TYPE_LABEL: Record<PhotoType, MsgKey> = {
  normal: "pz.type.normal",
  stage: "pz.type.stage",
  limited: "pz.type.limited",
};

export interface Puzzle {
  /**
   * 服务端分配的 `pz_NNNN`；**`null` = 服务端还不知道这一行**（本地新建、尚未提交）。
   *
   * 用 null 而不是 `tmp_N` 那种前缀约定：ID 会随请求体跨过网络边界，
   * 把「有没有持久化」这个状态编码进 ID 字符串，状态就跟着一起过去了 ——
   * 实际后果是本地占位 ID 流进了 7 个版本的 `cfg_puzzle`。
   * null 不可能被误当成合法 ID 发出去，这条由类型系统管，不靠人守约定。
   */
  puzzleId: string | null;
  /**
   * 纯本地标识：React key、列表定位、编辑器选中态都用它。**永不进请求体。**
   * 服务端 ID 只在行已持久化后才有，新建行在提交前也需要能被唯一指认。
   */
  localKey: string;
  idolId: string;
  seq: number;
  imgLocked: string;
  imgUnlocked: string;
  photoType: PhotoType;
  rewardDice: number;
  /** 亲密度展示门槛，0 = 无门槛。纯展示用，兑换不校验 */
  unlockIntimacyLv: number;
  /** 拼完给这位 idol 多少亲密度，0 = 不给。只在玩家第一次拼完时给，受亲密度每日上限截断 */
  intimacyPoints: number;
  /**
   * 使用关卡编号（「大关-小关」，如 1-1、4-5），按数值排好序。**空数组 = 不限**。
   * 纯编号：服务端只存 + 下发、不判定兑换，客户端按编号决定在哪用。
   */
  levelCodes: string[];
  /** 拼块数覆盖：null = 跟价目表；有值只能是 PUZZLE_GRID_OPTIONS（4/5/6）。纯表现量，碎片价仍按序号 */
  gridN: number | null;
  enabled: boolean;
}

/** 拼图「拼块数」下拉与价目表下拉的可选规格（2026-10-16：4×4 / 5×5 / 6×6，3×3 取消）。与后端 PUZZLE_GRID_OPTIONS 一致 */
export const PUZZLE_GRID_OPTIONS = [4, 5, 6] as const;

/** 价目表一行：seq → 网格边长 + 碎片价 */
export interface PuzzlePrice {
  seq: number;
  gridN: number;
  priceFrag: number;
}

/** 服务端行的形状（GET /config/versions/:v/puzzles） */
export interface ServerPuzzle {
  puzzleId: string;
  /** 稳定身份；老服务端没有 */
  uid?: string;
  idolId: string;
  seq: number;
  imgLocked: string;
  imgUnlocked: string;
  photoType: string;
  rewardDice: number;
  /** 亲密度展示门槛，0 = 无门槛。纯展示用，兑换不校验 */
  unlockIntimacyLv: number;
  /** 拼完给的亲密度；老服务端没有 → 当 0 */
  intimacyPoints?: number;
  /** 使用关卡编号；老服务端没有 → 当空 */
  levelCodes?: string[];
  /** 拼块数覆盖；老服务端没有 → 当 null（跟价目表） */
  gridN?: number | null;
  enabled: boolean;
}

/**
 * 服务端 → 前端。
 *
 * `photo_type` 是 `enumish`（varchar），取值靠约定，库里**可能出现约定外的值**。
 * 这里**原样保留**，不静默改写成 normal —— 改写之后界面显示「普通写真」、
 * 发布却按原值报 `PUZZLE_PHOTO_TYPE`，运营对着一个看起来正常的下拉无从下手。
 * 界面负责把未知值标出来（`isKnownPhotoType`），让人看得见才改得掉。
 */
export const toPuzzle = (r: ServerPuzzle): Puzzle => ({
  puzzleId: r.puzzleId,
  /*
   * 本地键 = 服务端存的 uid（新建时由本地生成、随建行请求落库）：所有人屏幕上同一张拼图的
   * 本地键一样，协同合并与回包丢失后的认领都按它认。老服务端 / 没回填到的退回 puzzleId
   */
  localKey: r.uid || r.puzzleId,
  idolId: r.idolId,
  seq: Number(r.seq),
  imgLocked: r.imgLocked ?? "",
  imgUnlocked: r.imgUnlocked ?? "",
  photoType: (r.photoType ?? "") as PhotoType,
  rewardDice: Number(r.rewardDice),
  unlockIntimacyLv: Number(r.unlockIntimacyLv ?? 0),
  intimacyPoints: Number(r.intimacyPoints ?? 0),
  levelCodes: Array.isArray(r.levelCodes) ? r.levelCodes.filter((x) => typeof x === "string") : [],
  gridN: r.gridN == null ? null : Number(r.gridN),
  enabled: !!r.enabled,
});

/**
 * 前端 → 服务端的**内容部分**。不含 puzzleId：
 * 新建时它还不存在，更新时由调用方从 `p.puzzleId` 单独取（那时已确定非 null）。
 */
export const toServerPuzzleBody = (p: Puzzle): PuzzleBody => ({
  uid: p.localKey,
  idolId: p.idolId,
  seq: p.seq,
  imgLocked: p.imgLocked,
  imgUnlocked: p.imgUnlocked,
  photoType: p.photoType,
  rewardDice: p.rewardDice,
  unlockIntimacyLv: p.unlockIntimacyLv,
  intimacyPoints: p.intimacyPoints,
  levelCodes: p.levelCodes,
  gridN: p.gridN,
  enabled: p.enabled,
});

/** 请求体里的内容字段（POST 新建 / PUT 更新共用） */
export type PuzzleBody = Omit<ServerPuzzle, "puzzleId">;

/**
 * 这一行服务端还不知道。
 *
 * 判据是**有没有服务端 ID**，不是 ID 长什么样。上一版用 `puzzleId.startsWith("tmp_")`，
 * 等于把「已否持久化」编码进 ID 字符串——而 ID 会跟着请求体出网，状态就一起泄漏了。
 */
export const isNewPuzzle = (p: Puzzle) => p.puzzleId == null;

/**
 * 新建一张。默认启用、普通写真，奖励留 0 由策划自己填——替他猜等于塞假配置。
 *
 * `localKey` 是 UUID：随建行请求落库成这一行的 uid，所有人屏幕上同一张拼图的本地键都一样。
 */
export const newPuzzle = (idolId: string, seq: number): Puzzle => ({
  puzzleId: null, localKey: globalThis.crypto.randomUUID(), idolId, seq, imgLocked: "", imgUnlocked: "",
  photoType: "normal", rewardDice: 0, unlockIntimacyLv: 0, intimacyPoints: 0, levelCodes: [], gridN: null, enabled: true,
});

/** 这张实际用的拼块数：自己的覆盖优先，否则价目表按序号带出；都没有为 null */
export const effectiveGridN = (p: Puzzle, prices: PuzzlePrice[]): number | null =>
  p.gridN ?? prices.find((x) => x.seq === p.seq)?.gridN ?? null;

/**
 * 网格边长上限。**必须与后端 `config-validate.ts` 的 `GRID_N_MAX` 一致** ——
 * 这边只负责当场标红，真正拦下来的是发布闸。两边不一致的表现是
 * 「界面上看着没问题、发布时被拒」，而报错指向的是一个界面从没提示过的规则。
 */
export const GRID_N_MAX_UI = 8;

/**
 * 碎片价上限。同上：**必须与后端 `config-validate.ts` 的 `INT32_MAX` 一致**。
 * 不是审美取值，是 `price_frag` 那一列存得下的最大整数。
 */
export const PRICE_FRAG_MAX_UI = 2147483647;

/**
 * 价目表一行填得对不对（i18n key，null = 没问题）。
 *
 * **口径抄的是发布闸**（`config-validate.ts` 的 `PUZZLE_PRICE_POSITIVE` /
 * `PUZZLE_GRID_N`），不是自己定的。碎片价是 `> 0` 而不是 `>= 0`——
 * 0 表示「不花碎片就能兑」，服务端写入闸和发布闸都会拒，
 * 界面这边要是按 `>= 0` 放行，就成了「存得下、发布被拒」，
 * 而报错指向一条界面从没提示过的规则。
 *
 * 空值同样落在这里：`priceFrag` 是 number，清空在 `patch` 里会变成 0，
 * 于是「空」和「填了 0」殊途同归，都被这一条拦下——两者要做的事也确实是同一件。
 */
export function priceRowIssue(p: PuzzlePrice): MsgKey | null {
  if (!Number.isInteger(p.gridN) || p.gridN < 2 || p.gridN > GRID_N_MAX_UI) return "pz.price.badGrid";
  if (!Number.isInteger(p.priceFrag) || p.priceFrag <= 0) return "pz.price.badFrag";
  if (p.priceFrag > PRICE_FRAG_MAX_UI) return "pz.price.fragTooBig";
  return null;
}

/** 价目表默认值：碎片价初始取 gridN²，之后可独立改 */
export const newPrice = (seq: number, gridN = 4): PuzzlePrice => ({ seq, gridN, priceFrag: gridN * gridN });

/** 取值是否在约定集合里。false = 库里是脏数据，界面要标红而不是假装正常 */
export const isKnownPhotoType = (v: string): v is PhotoType => (PHOTO_TYPES as string[]).includes(v);

export const priceOf = (prices: PuzzlePrice[], seq: number): PuzzlePrice | undefined =>
  prices.find((p) => p.seq === seq);

/**
 * 单张拼图的问题清单（i18n key）。发布闸的**子集** —— 这里只做能在前端判的，
 * 授权有效性与 idol 上架状态由服务端在发布时兜底。
 *
 * 前端先报是为了让人在配置时就看见，不用等到发布被拒才回来一条条找。
 */
export function puzzleIssues(p: Puzzle, prices: PuzzlePrice[]): MsgKey[] {
  // 顺序 = 弹窗里的字段顺序。`puzzleBlocker` 取第一条当「还差什么」，
  // 顺序错了就会先喊最下面那个缺的，人得倒着往上找
  const out: MsgKey[] = [];
  if (!priceOf(prices, p.seq)) out.push("pz.iss.noPrice");
  if (!p.imgLocked || !p.imgUnlocked) out.push("pz.iss.noImg");
  if (!isKnownPhotoType(p.photoType)) out.push("pz.iss.badType");
  if (!(p.rewardDice > 0)) out.push("pz.iss.noReward");
  return out;
}

/**
 * 存不下去的第一条原因；`null` = 能存。**保存闸的唯一口径。**
 *
 * 从 `puzzleIssues` 取第一条，不另写一串判断。原先弹窗里手抄了一份平行的判断，
 * 抄漏了「缺图」和「脏写真类型」两条 —— 于是能存下一张在列表里明明标着 ✗ 的拼图，
 * 只靠列表底色事后追认，而底色拦不住任何人。两处各写一份就一定会这样漂移，
 * 这里让它没得漂：能存下的行，`puzzleIssues` 必然为空。
 */
export function puzzleBlocker(p: Puzzle, prices: PuzzlePrice[]): MsgKey | null {
  if (!p.idolId) return "pz.needIdol";
  const first = puzzleIssues(p, prices)[0];
  // 缺价目行在弹窗里要指路去价目表：序号建完就锁死改不动，只能去补一行 seq。
  // 列表里不需要这句，所以两处文案不同、判断相同
  return first === "pz.iss.noPrice" ? "pz.noPriceRow" : (first ?? null);
}

/**
 * 同一 idol 的启用行是否组成从 1 起的连续前缀。返回缺的那个 seq，没问题返回 null。
 *
 * **只看启用行** —— 与发布闸 `PUZZLE_SEQ_CONTINUOUS` 同一口径：
 * seq1 停用而 seq2 启用时玩家兑不了 seq1，seq2 的前序条件就永远不满足，
 * 那是一张谁也拿不到的图。
 */
/**
 * 一串序号里第一个断掉的号（应当从 1 起连续）；没断返回 null。
 *
 * 抽出来是因为 `findIndex((s, i) => s !== i + 1)` 差一位就全错，
 * 而它原本在这个文件里就抄了两遍。
 */
const gapFrom1 = (seqs: number[]): number | null => {
  const sorted = [...seqs].sort((a, b) => a - b);
  const gap = sorted.findIndex((s, i) => s !== i + 1);
  return gap >= 0 ? gap + 1 : null;
};

export function seqGapOf(puzzles: Puzzle[], idolId: string): number | null {
  return gapFrom1(puzzles.filter((p) => p.idolId === idolId && p.enabled).map((p) => p.seq));
}

/**
 * 新建拼图时**可选的序号**。
 *
 *   价目表里有的 seq  ∩  (这位 idol 的空洞 ∪ 下一个)
 *
 * 两个约束合成一个列表,于是它们在新建路径上都变得不可表达:
 * - `PUZZLE_SEQ_HAS_PRICE`：没价目行的号不在列表里
 * - `PUZZLE_SEQ_CONTINUOUS`：大于「下一个」的号不在列表里,选不出断号
 * - 重号：已用过的号不在列表里
 *
 * 空列表 = 价目阶梯不够长,得先去价目表补一行——这是唯一还需要人去别处的情况。
 */
export function availableSeqs(puzzles: Puzzle[], idolId: string, prices: PuzzlePrice[]): number[] {
  const used = new Set(puzzles.filter((p) => p.idolId === idolId).map((p) => p.seq));
  const next = Math.max(0, ...used) + 1;
  return prices.map((p) => p.seq).filter((s) => !used.has(s) && s <= next).sort((a, b) => a - b);
}

/** 这位 idol 下一张是第几张。价目表缺这一档时用来告诉运营去补哪一行 */
export const nextSeqOf = (puzzles: Puzzle[], idolId: string): number =>
  Math.max(0, ...puzzles.filter((p) => p.idolId === idolId).map((p) => p.seq)) + 1;

/** 价目表本身的断号（从 1 起连续）。返回缺的 seq，没问题返回 null */
export function priceGapOf(prices: PuzzlePrice[]): number | null {
  return gapFrom1(prices.map((p) => p.seq));
}


/** 「大关-小关」编号：各 1 起、最多 3 位。与后端 game/puzzle.ts 的 LEVEL_CODE_RE 一致 */
export const LEVEL_CODE_RE = /^([1-9]\d{0,2})-([1-9]\d{0,2})$/;
const RANGE_RE = /^([1-9]\d{0,2})-([1-9]\d{0,2})\s*[~～]\s*([1-9]\d{0,2})-([1-9]\d{0,2})$/;
/** 一张拼图最多配多少个编号（与后端 PUZZLE_LEVEL_CODES_MAX 一致） */
export const LEVEL_CODES_MAX = 200;

/** 按 (大关, 小关) 数值排序：1-10 在 1-3 之后 */
export function sortLevelCodes(codes: string[]): string[] {
  const k = (c: string) => { const m = LEVEL_CODE_RE.exec(c); return m ? [Number(m[1]), Number(m[2])] : [Infinity, Infinity]; };
  return [...codes].sort((a, b) => { const x = k(a), y = k(b); return x[0] - y[0] || x[1] - y[1] || a.localeCompare(b); });
}

/**
 * 管端「使用关卡」输入 → 编号列表。逗号 / 空格 / 换行 / 顿号分隔；支持范围「1-1~1-5」，**只能在同一大关内**，
 * 展开成明确列表（存储与下发只有一种形态）。结果去重、按数值排序。有任何一段不合法就整体报错、不改动。
 */
export function parseLevelCodesInput(text: string, existing: string[] = []):
  { ok: true; codes: string[] } | { ok: false; error: "pz.code.bad" | "pz.code.crossRange" | "pz.code.reversed" | "pz.code.tooMany"; part: string } {
  const out = new Set(existing);
  // 范围里的「~」两边允许空格：先把它们并起来，再按分隔符切
  for (const part of text.replace(/\s*[~～]\s*/g, "~").split(/[\s,，、;；]+/).filter(Boolean)) {
    const r = RANGE_RE.exec(part);
    if (r) {
      if (r[1] !== r[3]) return { ok: false, error: "pz.code.crossRange", part };
      const a = Number(r[2]), b = Number(r[4]);
      if (a > b) return { ok: false, error: "pz.code.reversed", part };
      for (let i = a; i <= b; i++) out.add(`${Number(r[1])}-${i}`);
    } else if (LEVEL_CODE_RE.test(part)) out.add(part);
    else return { ok: false, error: "pz.code.bad", part };
    if (out.size > LEVEL_CODES_MAX) return { ok: false, error: "pz.code.tooMany", part };
  }
  return { ok: true, codes: sortLevelCodes([...out]) };
}
